import {Image, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {ACTIVITY_STATUS_COLORS, useTrendLabels} from '../data/labels'
import {type Trend, type TrendAvatar} from '../data/trends'
import {getVoicesDisplay} from '../util'
import {ImageWithPlaceholder} from './ImageWithPlaceholder'
import {TrendHeroBadges} from './TrendHeroBadges'
import {TrendImageFallback} from './TrendImageFallback'

/**
 * Trend metadata header rendered above the Bluesky-native search results
 * whenever the current `/search` query matches a known trend.
 *
 * Mirrors Figma node `384:53825` in file `C8J3bA4ThNVCG0vzlHeI16`. The user's
 * mental model: trend detail isn't a separate screen — it's a search-result
 * enrichment. See `trends-how-it-works.md` for the routing contract.
 *
 * Reuses the avatar-stack + active-voices treatment from the other trend
 * cards, but the title is significantly larger (32px vs the hero's 24). Below
 * it we surface only the new velocity + signal badges (`TrendHeroBadges`),
 * which self-hide when absent. The legacy media/searches/sources KPIs are
 * intentionally never shown.
 */
export function TrendSearchHeader({trend}: {trend: Trend}) {
  const t = useTheme()
  const {_} = useLingui()
  const {getCategoryLabel, activityStatusLabels} = useTrendLabels()

  const activityStatusColor = ACTIVITY_STATUS_COLORS[trend.activityStatus]
  // Undefined when the backend sends a status we have no label for — the
  // row then renders the category alone (no separator dot).
  const activityStatusLabel = activityStatusLabels[trend.activityStatus] as
    | string
    | undefined
  const visibleAvatars = trend.participantAvatars.slice(0, 5)
  const voices = getVoicesDisplay(trend.metrics.activeVoicesCount)

  // The backend often sets heroImage to a copy of one of the articles' images.
  // If the hero matches ANY article image, drop the hero here — the article
  // carousel below already shows it, so we avoid the same picture twice. (A
  // null heroImage is never a duplicate, so the brand fallback still renders.)
  const heroDuplicatesAnyCard =
    !!trend.heroImage &&
    trend.featuredCards.some(card => card.image?.url === trend.heroImage!.url)

  // If every article card is already a fallback, don't add a hero fallback on
  // top — a wall of placeholders looks broken. (Only applies when there are
  // cards; with no cards the hero fallback still provides some visual.)
  const allCardsAreFallback =
    trend.featuredCards.length > 0 &&
    trend.featuredCards.every(card => !card.image)

  const heroFallback = (
    <TrendImageFallback
      style={[a.w_full, a.h_full]}
      variant={trend.featuredCards.length > 0 ? 'globe' : 'brand'}
    />
  )

  return (
    <View
      style={[
        a.w_full,
        a.gap_xl,
        a.px_lg,
        {paddingTop: 24, paddingBottom: 32},
      ]}>
      <View style={[a.gap_md, {paddingBottom: 8}]}>
        <View style={[a.flex_row, a.align_center, a.gap_xs]}>
          <Text style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
            {getCategoryLabel(trend.category)}
          </Text>
          {/* Dot + status render together — no dangling dot when the
              backend sends a status we have no label for. */}
          {!!activityStatusLabel && (
            <>
              <View
                style={[
                  a.rounded_full,
                  {
                    width: 4,
                    height: 4,
                    backgroundColor: t.atoms.text_contrast_medium.color,
                    opacity: 0.97,
                  },
                ]}
              />
              <Text
                style={[
                  a.text_xs,
                  a.font_medium,
                  {color: activityStatusColor},
                ]}>
                {activityStatusLabel}
              </Text>
            </>
          )}
        </View>

        {voices.showVoices && (
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            {visibleAvatars.length > 0 && (
              <AvatarStack avatars={visibleAvatars} />
            )}
            <Text
              style={[a.text_xs, a.font_medium, t.atoms.text_contrast_medium]}>
              {voices.showCount &&
                `${formatCount(trend.metrics.activeVoicesCount ?? 0)} `}
              {_(msg`active voices`)}
            </Text>
          </View>
        )}
      </View>

      <View style={[{gap: 12}]}>
        <Text
          style={[a.font_bold, t.atoms.text, {fontSize: 32, lineHeight: 35}]}>
          {trend.title}
        </Text>

        {trend.description ? (
          <Text
            style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
            {trend.description}
          </Text>
        ) : null}

        {/* New velocity + signal badges only; self-hides when the trend has
            neither. The legacy media/searches/sources KPIs are intentionally
            never shown. */}
        <TrendHeroBadges trend={trend} />
      </View>

      {/* With an image, the fallback still renders underneath and the image is
          drawn over it once loaded - a slow or blocked publisher shows the
          fallback rather than a blank. Same ratio as the small carousel trend
          cards (176×92). */}
      {heroDuplicatesAnyCard ? null : trend.heroImage ? (
        <ImageWithPlaceholder
          uri={trend.heroImage.url}
          style={[a.w_full, {aspectRatio: 176 / 92, borderRadius: 16}]}
          placeholder={heroFallback}
        />
      ) : allCardsAreFallback ? null : (
        // Standalone, the fallback needs the same box the image would have had
        // (it only fills its parent).
        <View
          style={[
            a.w_full,
            a.overflow_hidden,
            {aspectRatio: 176 / 92, borderRadius: 16},
          ]}>
          {heroFallback}
        </View>
      )}
    </View>
  )
}

function AvatarStack({avatars}: {avatars: TrendAvatar[]}) {
  const ss = useSemanticTokens()
  return (
    <View style={[a.flex_row, a.align_center]}>
      {avatars.map((avatar, i) => (
        <Image
          key={avatar.did}
          accessibilityIgnoresInvertColors
          source={{uri: avatar.avatarUrl}}
          style={[
            a.rounded_full,
            {
              width: 24,
              height: 24,
              marginLeft: i === 0 ? 0 : -8,
              borderWidth: 1,
              borderColor: 'rgba(0,0,0,0.2)',
              backgroundColor: ss.bg.contrast_25,
            },
          ]}
        />
      ))}
    </View>
  )
}

/* ---------- formatting + labels ---------- */

function formatCount(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}
